import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Output,
  ViewChild,
} from "@angular/core";

@Component({
  selector: "evrideo-slide-to-confirm",
  templateUrl: "./slide-to-confirm.component.html",
  styleUrls: ["./slide-to-confirm.component.scss"],
})
export class SlideToConfirmComponent {
  @ViewChild("track") trackRef: ElementRef<HTMLElement>;

  @Output() confirmed = new EventEmitter<void>();

  positionPercent = 0;
  isDragging = false;
  isConfirmed = false;

  private readonly _trackPadding = 4;
  private readonly _thumbWidth = 40;

  get trackElement(): HTMLElement | null {
    return this.trackRef?.nativeElement ?? null;
  }

  get thumbLeftStyle(): string {
    return `calc(${this._trackPadding}px + (100% - ${this._trackPadding * 2 + this._thumbWidth}px) * ${this.positionPercent / 100})`;
  }

  get fillWidthStyle(): string {
    return `calc((100% - ${this._trackPadding * 2 + this._thumbWidth}px) * ${this.positionPercent / 100})`;
  }

  private _getPositionFromEvent(clientX: number): number {
    const track = this.trackElement;
    if (!track) return 0;
    const rect = track.getBoundingClientRect();
    const x = clientX - rect.left;
    const travelWidth = rect.width - this._trackPadding * 2 - this._thumbWidth;
    if (travelWidth <= 0) return 0;
    const thumbLeft = Math.max(
      this._trackPadding,
      Math.min(rect.width - this._trackPadding - this._thumbWidth, x - this._thumbWidth / 2),
    );
    const percent = ((thumbLeft - this._trackPadding) / travelWidth) * 100;
    return Math.max(0, Math.min(100, percent));
  }

  private _emitIfReachedEnd(percent: number): void {
    if (percent >= 95 && !this.isConfirmed) {
      this.isConfirmed = true;
      this.positionPercent = 100;
      this.confirmed.emit();
    }
  }

  onHandlePointerDown(event: PointerEvent): void {
    if (this.isConfirmed) return;
    event.preventDefault();
    this.isDragging = true;
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
  }

  @HostListener("document:pointermove", ["$event"])
  onPointerMove(event: PointerEvent): void {
    if (!this.isDragging || this.isConfirmed) return;
    const track = this.trackElement;
    if (!track) return;
    const percent = this._getPositionFromEvent(event.clientX);
    this.positionPercent = percent;
    this._emitIfReachedEnd(percent);
  }

  @HostListener("document:pointerup", ["$event"])
  @HostListener("document:pointercancel", ["$event"])
  onPointerUp(_event?: PointerEvent): void {
    if (!this.isDragging) return;
    this.isDragging = false;
    if (!this.isConfirmed) {
      this.positionPercent = 0;
    }
  }
}
